/**
 * Comprehensive polyfills for old browsers and Chromebooks
 * Fixes AWS Amplify API calls failing due to missing modern JavaScript features
 */

// Import polyfill packages for better compatibility
import 'core-js/stable';

// Browser detection utilities
const getBrowserInfo = () => {
  const userAgent = navigator.userAgent;
  const isChrome = /Chrome/.test(userAgent) && /Google Inc/.test(navigator.vendor);
  const isChromebook = /CrOS/.test(userAgent);
  const isSafari = /Safari/.test(userAgent) && !/Chrome/.test(userAgent);
  const isFirefox = /Firefox/.test(userAgent);
  const isEdge = /Edg/.test(userAgent);
  
  const versionMatch = userAgent.match(/(Chrome|Safari|Firefox|Edg)\/(\d+)/);
  const version = versionMatch ? parseInt(versionMatch[2], 10) : 0;
  
  return {
    isChrome,
    isChromebook,
    isSafari,
    isFirefox,
    isEdge,
    version,
    isOldBrowser: version < 80 || (isSafari && version < 14) || (isFirefox && version < 78)
  };
};

const browserInfo = getBrowserInfo();

// Core polyfills for old browsers
if (typeof window !== 'undefined') {
  
  // URL.canParse polyfill for AWS Amplify compatibility
  // This is needed because URL.canParse is very new (2021-2023) and not in all polyfill libraries
  if (window.URL && !window.URL.canParse) {
    window.URL.canParse = function(url, base) {
      try {
        new URL(url, base);
        return true;
      } catch (e) {
        return false;
      }
    };
  }

  // AWS Amplify URL compatibility fix
  // Fix for AWS Amplify's AmplifyUrl.canParse method
  if (window.URL && !window.URL.prototype.canParse) {
    window.URL.prototype.canParse = function(url, base) {
      try {
        new URL(url, base);
        return true;
      } catch (e) {
        return false;
      }
    };
  }

  // Create AmplifyUrl class for AWS Amplify compatibility
  if (!window.AmplifyUrl) {
    window.AmplifyUrl = class extends URL {
      static canParse(url, base) {
        try {
          new URL(url, base);
          return true;
        } catch (e) {
          return false;
        }
      }
    };
  }

  // AWS Amplify utilities polyfill
  if (!window.AmplifyUtils) {
    window.AmplifyUtils = {
      canParse: function(url, base) {
        try {
          new URL(url, base);
          return true;
        } catch (e) {
          return false;
        }
      }
    };
  }

  // TextEncoder/TextDecoder polyfills for AWS Amplify
  if (!window.TextEncoder) {
    window.TextEncoder = function() {
      this.encode = function(str) {
        const utf8 = [];
        for (let i = 0; i < str.length; i++) {
          let charCode = str.charCodeAt(i);
          if (charCode < 0x80) {
            utf8.push(charCode);
          } else if (charCode < 0x800) {
            utf8.push(0xc0 | (charCode >> 6), 0x80 | (charCode & 0x3f));
          } else if (charCode < 0xd800 || charCode >= 0xe000) {
            utf8.push(0xe0 | (charCode >> 12), 0x80 | ((charCode >> 6) & 0x3f), 0x80 | (charCode & 0x3f));
          } else {
            i++;
            charCode = 0x10000 + (((charCode & 0x3ff) << 10) | (str.charCodeAt(i) & 0x3ff));
            utf8.push(0xf0 | (charCode >> 18), 0x80 | ((charCode >> 12) & 0x3f), 0x80 | ((charCode >> 6) & 0x3f), 0x80 | (charCode & 0x3f));
          }
        }
        return new Uint8Array(utf8);
      };
    };
  }

  if (!window.TextDecoder) {
    window.TextDecoder = function() {
      this.decode = function(bytes) {
        let result = '';
        let i = 0;
        while (i < bytes.length) {
          let c = bytes[i++];
          if (c > 127) {
            if (c > 191 && c < 224) {
              c = (c & 31) << 6 | bytes[i++] & 63;
            } else if (c > 223 && c < 240) {
              c = (c & 15) << 12 | (bytes[i++] & 63) << 6 | bytes[i++] & 63;
            } else if (c > 239 && c < 248) {
              c = (c & 7) << 18 | (bytes[i++] & 63) << 12 | (bytes[i++] & 63) << 6 | bytes[i++] & 63;
            }
          }
          if (c <= 0xFFFF) {
            result += String.fromCharCode(c);
          } else if (c <= 0x10FFFF) {
            c -= 0x10000;
            result += String.fromCharCode(0xD800 | (c >> 10));
            result += String.fromCharCode(0xDC00 | (c & 0x3FF));
          }
        }
        return result;
      };
    };
  }

  if (!String.prototype.trim) {
    String.prototype.trim = function() {
      return this.replace(/^[\s\uFEFF\xA0]+|[\s\uFEFF\xA0]+$/g, '');
    };
  }

  if (!String.prototype.startsWith) {
    String.prototype.startsWith = function(searchString, position) {
      position = position || 0;
      return this.substr(position, searchString.length) === searchString;
    };
  }

  if (!String.prototype.endsWith) {
    String.prototype.endsWith = function(searchString, length) {
      if (length === undefined || length > this.length) {
        length = this.length;
      }
      return this.substring(length - searchString.length, length) === searchString;
    };
  }

  if (!String.prototype.includes) {
    String.prototype.includes = function(search, start) {
      if (typeof start !== 'number') {
        start = 0;
      }
      if (start + search.length > this.length) {
        return false;
      } else {
        return this.indexOf(search, start) !== -1;
      }
    };
  }

  if (!String.prototype.replaceAll) {
    String.prototype.replaceAll = function(search, replace) {
      if (search instanceof RegExp) {
        if (!search.global) {
          throw new TypeError('String.prototype.replaceAll called with a non-global RegExp argument');
        }
        return this.replace(search, replace);
      }
      return this.replace(new RegExp(search.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), replace);
    };
  }

  // Object.keys polyfill
  if (!Object.keys) {
    Object.keys = function(obj) {
      const keys = [];
      for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
          keys.push(key);
        }
      }
      return keys;
    };
  }

  // Object.assign polyfill
  if (!Object.assign) {
    Object.assign = function(target) {
      if (target == null) {
        throw new TypeError('Cannot convert undefined or null to object');
      }
      const to = Object(target);
      for (let index = 1; index < arguments.length; index++) {
        const nextSource = arguments[index];
        if (nextSource != null) {
          for (const nextKey in nextSource) {
            if (Object.prototype.hasOwnProperty.call(nextSource, nextKey)) {
              to[nextKey] = nextSource[nextKey];
            }
          }
        }
      }
      return to;
    };
  }

  // Object.fromEntries polyfill
  if (!Object.fromEntries) {
    Object.fromEntries = function(iterable) {
      if (iterable == null) {
        throw new TypeError('Object.fromEntries requires an iterable argument');
      }
      const obj = {};
      if (Array.isArray(iterable)) {
        for (let i = 0; i < iterable.length; i++) {
          const pair = iterable[i];
          if (pair == null) {
            throw new TypeError('Object.fromEntries requires each entry to be a non-null object');
          }
          if (typeof pair !== 'object') {
            throw new TypeError('Object.fromEntries requires each entry to be an object');
          }
          const key = pair[0];
          const value = pair[1];
          obj[key] = value;
        }
      } else {
        // Handle other iterables if needed
        const iterator = iterable[Symbol.iterator];
        if (typeof iterator === 'function') {
          const it = iterator.call(iterable);
          let result = it.next();
          while (!result.done) {
            const pair = result.value;
            if (pair == null) {
              throw new TypeError('Object.fromEntries requires each entry to be a non-null object');
            }
            if (typeof pair !== 'object') {
              throw new TypeError('Object.fromEntries requires each entry to be an object');
            }
            const key = pair[0];
            const value = pair[1];
            obj[key] = value;
            result = it.next();
          }
        }
      }
      return obj;
    };
  }

  // Array.from polyfill
  if (!Array.from) {
    Array.from = function(arrayLike, mapFn, thisArg) {
      const C = this;
      const items = Object(arrayLike);
      if (arrayLike == null) {
        throw new TypeError('Array.from requires an array-like object - not null or undefined');
      }
      const mapFunction = mapFn ? (typeof mapFn === 'function' ? mapFn : function(x) { return x; }) : undefined;
      const len = parseInt(items.length) || 0;
      const A = typeof C === 'function' ? Object(new C(len)) : new Array(len);
      let k = 0;
      let kValue;
      while (k < len) {
        kValue = items[k];
        if (mapFunction) {
          A[k] = typeof thisArg === 'undefined' ? mapFunction(kValue, k) : mapFunction.call(thisArg, kValue, k);
        } else {
          A[k] = kValue;
        }
        k += 1;
      }
      A.length = len;
      return A;
    };
  }

  if (!Array.prototype.find) {
    Array.prototype.find = function(predicate, thisArg) {
      if (this == null) {
        throw new TypeError('Array.prototype.find called on null or undefined');
      }
      if (typeof predicate !== 'function') {
        throw new TypeError('predicate must be a function');
      }
      const list = Object(this);
      const length = parseInt(list.length) || 0;
      let value;
      for (let i = 0; i < length; i++) {
        value = list[i];
        if (predicate.call(thisArg, value, i, list)) {
          return value;
        }
      }
      return undefined;
    };
  }

  if (!Array.prototype.filter) {
    Array.prototype.filter = function(fun, thisArg) {
      if (this == null) {
        throw new TypeError('Array.prototype.filter called on null or undefined');
      }
      if (typeof fun !== 'function') {
        throw new TypeError('fun must be a function');
      }
      const list = Object(this);
      const length = parseInt(list.length) || 0;
      const res = [];
      for (let i = 0; i < length; i++) {
        if (i in list) {
          const val = list[i];
          if (fun.call(thisArg, val, i, list)) {
            res.push(val);
          }
        }
      }
      return res;
    };
  }

  if (!Array.prototype.flatMap) {
    Array.prototype.flatMap = function(callback, thisArg) {
      if (this == null) {
        throw new TypeError('Array.prototype.flatMap called on null or undefined');
      }
      if (typeof callback !== 'function') {
        throw new TypeError('callback must be a function');
      }
      const list = Object(this);
      const length = parseInt(list.length) || 0;
      const result = [];
      for (let i = 0; i < length; i++) {
        if (i in list) {
          const mapped = callback.call(thisArg, list[i], i, list);
          if (Array.isArray(mapped)) {
            result.push(...mapped);
          } else {
            result.push(mapped);
          }
        }
      }
      return result;
    };
  }

  if (!Array.prototype.flat) {
    Array.prototype.flat = function(depth) {
      if (this == null) {
        throw new TypeError('Array.prototype.flat called on null or undefined');
      }
      const list = Object(this);
      const result = [];
      const flatten = (arr, currentDepth) => {
        for (let i = 0; i < arr.length; i++) {
          if (i in arr) {
            if (Array.isArray(arr[i]) && currentDepth > 0) {
              flatten(arr[i], currentDepth - 1);
            } else {
              result.push(arr[i]);
            }
          }
        }
      };
      flatten(list, depth === undefined ? 1 : depth);
      return result;
    };
  }

  if (!Array.prototype.at) {
    Array.prototype.at = function(index) {
      if (this == null) {
        throw new TypeError('Array.prototype.at called on null or undefined');
      }
      const list = Object(this);
      const length = parseInt(list.length) || 0;
      const relativeIndex = index < 0 ? length + index : index;
      if (relativeIndex < 0 || relativeIndex >= length) {
        return undefined;
      }
      return list[relativeIndex];
    };
  }

  // Promise polyfill for very old browsers
  if (!window.Promise) {
    window.Promise = function(executor) {
      const self = this;
      self.state = 'pending';
      self.value = undefined;
      self.handlers = [];

      function resolve(result) {
        if (self.state === 'pending') {
          self.state = 'fulfilled';
          self.value = result;
          self.handlers.forEach(handle);
          self.handlers = null;
        }
      }

      function reject(error) {
        if (self.state === 'pending') {
          self.state = 'rejected';
          self.value = error;
          self.handlers.forEach(handle);
          self.handlers = null;
        }
      }

      function handle(handler) {
        if (self.state === 'pending') {
          self.handlers.push(handler);
        } else {
          if (self.state === 'fulfilled' && typeof handler.onFulfilled === 'function') {
            handler.onFulfilled(self.value);
          }
          if (self.state === 'rejected' && typeof handler.onRejected === 'function') {
            handler.onRejected(self.value);
          }
        }
      }

      this.then = function(onFulfilled, onRejected) {
        return new Promise(function(resolve, reject) {
          handle({
            onFulfilled: function(result) {
              try {
                resolve(onFulfilled ? onFulfilled(result) : result);
              } catch (ex) {
                reject(ex);
              }
            },
            onRejected: function(error) {
              try {
                resolve(onRejected ? onRejected(error) : error);
              } catch (ex) {
                reject(ex);
              }
            }
          });
        });
      };

      this.catch = function(onRejected) {
        return this.then(null, onRejected);
      };

      try {
        executor(resolve, reject);
      } catch (ex) {
        reject(ex);
      }
    };
  }

  // JSON polyfill for very old browsers
  if (!window.JSON) {
    window.JSON = {
      stringify: function(value) {
        if (value === null) return 'null';
        if (typeof value === 'string') return '"' + value.replace(/"/g, '\\"') + '"';
        if (typeof value === 'number' || typeof value === 'boolean') return String(value);
        if (typeof value === 'object') {
          const pairs = [];
          for (const key in value) {
            if (Object.prototype.hasOwnProperty.call(value, key)) {
              pairs.push('"' + key + '":' + JSON.stringify(value[key]));
            }
          }
          return '{' + pairs.join(',') + '}';
        }
        return 'undefined';
      },
      parse: function(text) {
        return eval('(' + text + ')');
      }
    };
  }

  // Fetch polyfill for old browsers
  if (!window.fetch) {
    window.fetch = function(url, options) {
      return new Promise(function(resolve, reject) {
        const xhr = new XMLHttpRequest();
        const method = (options && options.method) || 'GET';
        const headers = (options && options.headers) || {};
        
        xhr.open(method, url);
        
        for (const key in headers) {
          if (Object.prototype.hasOwnProperty.call(headers, key)) {
            xhr.setRequestHeader(key, headers[key]);
          }
        }
        
        xhr.onload = function() {
          resolve({
            ok: xhr.status >= 200 && xhr.status < 300,
            status: xhr.status,
            statusText: xhr.statusText,
            json: function() {
              return Promise.resolve(JSON.parse(xhr.responseText));
            },
            text: function() {
              return Promise.resolve(xhr.responseText);
            }
          });
        };
        
        xhr.onerror = function() {
          reject(new Error('Network error'));
        };
        
        xhr.send((options && options.body) || null);
      });
    };
  }

  // Selection polyfills
  if (!window.EmptyRanges) {
    window.EmptyRanges = function() {
      try {
        const selection = document.getSelection();
        if (selection && typeof selection.removeAllRanges === 'function') {
          selection.removeAllRanges();
        } else if (selection && typeof selection.empty === 'function') {
          selection.empty();
        }
      } catch (error) {
        console.warn('EmptyRanges polyfill: Could not clear selection ranges', error);
      }
    };
  }

  if (!document.getSelection && window.getSelection) {
    document.getSelection = window.getSelection;
  }
  
  if (window.getSelection && !window.getSelection().removeAllRanges) {
    const originalGetSelection = window.getSelection;
    window.getSelection = function() {
      const selection = originalGetSelection.call(this);
      if (selection && !selection.removeAllRanges) {
        selection.removeAllRanges = function() {
          if (this.empty) {
            this.empty();
          } else {
            while (this.rangeCount > 0) {
              this.removeRange(this.getRangeAt(0));
            }
          }
        };
      }
      return selection;
    };
  }

  // Console polyfill for very old browsers
  if (!window.console) {
    window.console = {
      log: function() {},
      warn: function() {},
      error: function() {},
      info: function() {},
      debug: function() {}
    };
  }

  // Symbol polyfill for very old browsers
  if (!window.Symbol) {
    window.Symbol = function(description) {
      if (this instanceof Symbol) {
        throw new TypeError('Symbol is not a constructor');
      }
      return Symbol(description);
    };
    
    // Symbol.iterator polyfill
    if (!Symbol.iterator) {
      Symbol.iterator = Symbol('Symbol.iterator');
    }
    
    // Symbol.asyncIterator polyfill
    if (!Symbol.asyncIterator) {
      Symbol.asyncIterator = Symbol('Symbol.asyncIterator');
    }
  }

  

  // Additional AWS Amplify compatibility polyfills
  if (!window.crypto) {
    window.crypto = {
      getRandomValues: function(arr) {
        for (let i = 0; i < arr.length; i++) {
          arr[i] = Math.floor(Math.random() * 256);
        }
        return arr;
      }
    };
  }

  // Headers polyfill for AWS Amplify
  if (!window.Headers) {
    window.Headers = function(init) {
      this._headers = {};
      if (init) {
        if (Array.isArray(init)) {
          init.forEach(function(header) {
            this._headers[header[0]] = header[1];
          }, this);
        } else if (typeof init === 'object') {
          Object.keys(init).forEach(function(key) {
            this._headers[key] = init[key];
          }, this);
        }
      }
      
      this.get = function(name) {
        return this._headers[name.toLowerCase()] || null;
      };
      
      this.set = function(name, value) {
        this._headers[name.toLowerCase()] = value;
      };
      
      this.has = function(name) {
        return name.toLowerCase() in this._headers;
      };
      
      this.delete = function(name) {
        delete this._headers[name.toLowerCase()];
      };
    };
  }

  // Request polyfill for AWS Amplify
  if (!window.Request) {
    window.Request = function(input, init) {
      this.url = typeof input === 'string' ? input : input.url;
      this.method = (init && init.method) || 'GET';
      this.headers = new Headers(init && init.headers);
      this.body = init && init.body;
    };
  }

  // Response polyfill for AWS Amplify
  if (!window.Response) {
    window.Response = function(body, init) {
      this.body = body;
      this.status = (init && init.status) || 200;
      this.statusText = (init && init.statusText) || 'OK';
      this.headers = new Headers(init && init.headers);
      
      this.json = function() {
        return Promise.resolve(JSON.parse(this.body));
      };
      
      this.text = function() {
        return Promise.resolve(this.body);
      };
    };
  }

  // Add browser info to window for debugging
  window.browserInfo = browserInfo;
  
  // Log browser compatibility info
  if (browserInfo.isOldBrowser) {
    console.warn('🐛 Old browser detected:', {
      userAgent: navigator.userAgent,
      browserInfo: browserInfo,
      message: 'Some features may not work properly. Consider updating your browser.'
    });
  }
}

const polyfills = {};
export default polyfills;